"use client";

import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import type { ExchangeRate, SupportedCurrency, ExchangeRates } from "@/lib/utils/currency";

/**
 * API 응답 타입
 */
interface ExchangeRatesResponse {
  rates: Record<string, ExchangeRate>;
  baseCurrency: "KRW";
  lastUpdated: string;
  message?: string;
}

// 환율 로컬 캐시 설정
// v2: ExchangeRate 타입에 marketRate 필드가 추가되면서 기존 localStorage 캐시는
// marketRate가 없어 쿠폰 변환이 display_rate로 폴백된다. 키를 버전업해서 즉시 무효화.
const CACHE_KEY = "locarora-exchange-rates-cache-v2";
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24시간

/**
 * 환율 데이터를 가져오는 함수
 * PWA 오프라인 시 localStorage 캐시 사용
 */
async function fetchExchangeRates(): Promise<ExchangeRatesResponse> {
  try {
    const response = await fetch("/api/exchange-rates");

    if (!response.ok) {
      throw new Error(`Failed to fetch exchange rates: ${response.status}`);
    }

    const data = await response.json();

    // 성공 시 캐시 저장
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ data, timestamp: Date.now() })
        );
      } catch {
        // localStorage 용량 초과 등 에러 무시
      }
    }

    return data;
  } catch (error) {
    // 실패 시 캐시 사용
    if (typeof window !== "undefined") {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          // 24시간 이내 캐시면 사용
          if (Date.now() - timestamp < CACHE_MAX_AGE) {
            console.warn("[Exchange Rates] Using cached data (offline/error)");
            return data;
          }
        }
      } catch {
        // 캐시 파싱 에러 무시
      }
    }
    throw error;
  }
}

/**
 * 환율 데이터를 가져오는 React Query 훅
 * Optimized: Delays initial fetch to not block initial page load
 *
 * @example
 * const { rates, isLoading, error, getRate, convertPrice } = useExchangeRates();
 *
 * // 특정 통화 환율 조회
 * const usdRate = getRate("USD"); // 1350.5
 *
 * // 가격 변환
 * const priceInUSD = convertPrice(50000, "USD"); // 37.02
 */
export function useExchangeRates(initialRates?: Partial<ExchangeRates>) {
  // SSR에서 전달된 initialRates를 우선 사용. 없으면 localStorage 캐시에서 읽기.
  const [cachedRates] = useState<Partial<ExchangeRates>>(() => {
    // SSR 전달 환율이 있으면 localStorage보다 우선 (서버 데이터가 최신)
    if (initialRates && Object.keys(initialRates).length > 0) return initialRates;
    if (typeof window === "undefined") return {};
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const { data, timestamp } = JSON.parse(raw);
        if (Date.now() - timestamp < CACHE_MAX_AGE) {
          const ratesObj = (data?.rates || {}) as Partial<ExchangeRates>;
          const entries = Object.values(ratesObj).filter(Boolean);
          const allHaveMarketRate = entries.every(
            (e) => typeof e?.marketRate === "number" && e.marketRate > 0,
          );
          if (entries.length === 0 || allHaveMarketRate) return ratesObj;
          localStorage.removeItem(CACHE_KEY);
        }
      }
    } catch {
      // ignore parse errors
    }
    return {};
  });

  // Delay fetch to avoid blocking initial page load
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    // Use requestIdleCallback if available, otherwise use setTimeout
    if (typeof window !== "undefined") {
      if ("requestIdleCallback" in window) {
        const idleCallback = window.requestIdleCallback(
          () => setShouldFetch(true),
          { timeout: 2000 } // Max 2 seconds delay
        );
        return () => window.cancelIdleCallback(idleCallback);
      } else {
        // Fallback: delay by 500ms to let initial render complete
        const timeout = setTimeout(() => setShouldFetch(true), 500);
        return () => clearTimeout(timeout);
      }
    }
  }, []);

  // SSR 환율이 있으면 React Query initialData로 제공 → idle fetch 전에도 데이터 즉시 사용 가능
  const ssrInitialData: ExchangeRatesResponse | undefined =
    initialRates && Object.keys(initialRates).length > 0
      ? {
          rates: initialRates as Record<string, ExchangeRatesResponse["rates"][string]>,
          baseCurrency: "KRW",
          lastUpdated: new Date().toISOString(),
        }
      : undefined;

  const query = useQuery<ExchangeRatesResponse>({
    queryKey: ["exchange-rates"],
    queryFn: fetchExchangeRates,
    enabled: shouldFetch, // Only fetch when browser is idle
    initialData: ssrInitialData,
    // 5 minute staleTime keeps the rate snapshot fresh enough that a user
    // returning to a long-open checkout tab does not see UI prices that
    // diverge meaningfully from what the server will charge. Anything
    // shorter would burn API calls; anything longer reopens the silent
    // FX-drift gap (see 2026-05-02 fix discussion).
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60 * 24, // 24시간
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Refetch when the user comes back to the tab or remounts a page so
    // the displayed total never drifts more than 5 minutes from what the
    // payment route will use server-side.
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });

  /**
   * 특정 통화의 환율 가져오기
   *
   * @param currency - 통화 코드
   * @returns 환율 (1 외화 = X KRW) 또는 undefined
   */
  const getRate = (currency: SupportedCurrency): number | undefined => {
    if (currency === "KRW") return 1;
    return query.data?.rates[currency]?.rate;
  };

  /**
   * KRW 금액을 외화로 변환
   *
   * @param amountKRW - 원화 금액
   * @param currency - 대상 통화
   * @returns 변환된 금액 또는 원래 금액 (환율 없을 시)
   */
  const convertPrice = (
    amountKRW: number,
    currency: SupportedCurrency
  ): number => {
    if (currency === "KRW") return amountKRW;

    const rate = getRate(currency);
    if (!rate || rate <= 0) return amountKRW;

    return amountKRW / rate;
  };

  /**
   * 환율 정보 전체 가져오기
   *
   * @param currency - 통화 코드
   * @returns ExchangeRate 객체 또는 undefined
   */
  const getRateInfo = (currency: SupportedCurrency): ExchangeRate | undefined => {
    if (currency === "KRW") {
      return {
        code: "KRW",
        name: "원화",
        rate: 1,
        marketRate: 1,
        rateDate: new Date().toISOString().split("T")[0],
        isFallback: false,
        updatedAt: new Date().toISOString(),
      };
    }
    return query.data?.rates[currency];
  };

  /**
   * 모든 환율 데이터
   */
  const rates: Partial<ExchangeRates> = query.data?.rates || cachedRates;

  return {
    // Query state
    rates,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    lastUpdated: query.data?.lastUpdated,

    // Helper functions
    getRate,
    getRateInfo,
    convertPrice,

    // Query controls
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}

export type UseExchangeRatesReturn = ReturnType<typeof useExchangeRates>;
